import React, { useEffect, useId, useRef } from "react";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Task, BoardColumn as BoardColumnType } from "../../types";
import TaskCard from "./TaskCard";
import BoardDropIndicator from "./BoardDropIndicator";
import Tooltip from "../ui/Tooltip";
import SharedListMark from "../ui/SharedListMark";
import { useFlipAnimation } from "../../hooks/useFlipAnimation";
import { prefersReducedMotion } from "../../lib/prefersReducedMotion";

interface BoardColumnProps {
  column: BoardColumnType;
  viewMode: string;
  matchingTaskIds?: Set<string>;
  activeDragTask?: Task | null;
  overTaskId?: string | null;
  isOverEndZone?: boolean;
  onAddToColumn: (columnName: string) => void;
  onToggleTask: (id: string) => void;
  onEditTask: (task: Task) => void;
  onConvertToSubtask?: (id: string) => void;
  onDeleteList?: (listName: string) => void;
  // Task ID whose exit animation should be triggered externally (e.g.
  // after the "complete with incomplete subtasks" dialog is confirmed).
  exitAnimatingTaskId?: string | null;
  // Task ID currently highlighted by keyboard arrow navigation.
  focusedTaskId?: string | null;
  // A collapsed list keeps its header — title, count, add button — and
  // drops its cards, so a long list can be kept out of the way.
  collapsed?: boolean;
  // Present only where this list can collapse; the title becomes the toggle.
  onToggleCollapse?: () => void;
}

const BoardColumnComponent: React.FC<BoardColumnProps> = ({
  column,
  viewMode,
  matchingTaskIds,
  activeDragTask,
  overTaskId,
  isOverEndZone,
  onAddToColumn,
  onToggleTask,
  onEditTask,
  onConvertToSubtask,
  onDeleteList,
  exitAnimatingTaskId,
  focusedTaskId,
  collapsed = false,
  onToggleCollapse,
}) => {
  const taskListRef = useRef<HTMLDivElement>(null);
  const taskListId = useId();

  const isList = viewMode === "list" && !!column.listId;

  // Tasks hidden by the active filter still live in the DOM (they animate
  // out via the per-task collapse below). The badge count reflects only the
  // visible ones. The parent removes columns whose visible count hits zero,
  // so we never need to hide the column itself here.
  const visibleCount = matchingTaskIds
    ? column.tasks.filter((t) => matchingTaskIds.has(t.id)).length
    : column.tasks.length;

  // Only pass visible task IDs to SortableContext
  const taskIds = matchingTaskIds
    ? column.tasks.filter((t) => matchingTaskIds.has(t.id)).map((t) => t.id)
    : column.tasks.map((t) => t.id);

  // FLIP only when task order/set changes, not when sibling heights change
  const syncFlipPositions = useFlipAnimation(taskListRef, "[data-flip-id]", taskIds.join(","));

  // Opening a list from the keyboard — Enter or Space on its title, which
  // fires a click with no pointer detail — carries the focus on to the
  // list's first task, so the arrow keys carry on inside the list just
  // opened. A mouse click leaves the focus where it is. The first task can
  // open below the fold (a title near the bottom of the window), so it is
  // scrolled into view the way the arrow keys scroll.
  const focusFirstTaskRef = useRef(false);
  const handleToggleClick = (event: React.MouseEvent) => {
    focusFirstTaskRef.current = collapsed && event.detail === 0;
    onToggleCollapse?.();
  };
  const firstTaskId = taskIds[0];
  useEffect(() => {
    if (collapsed || !focusFirstTaskRef.current) return;
    focusFirstTaskRef.current = false;
    const cards = taskListRef.current?.querySelectorAll<HTMLElement>("[data-task-id]");
    const first = Array.from(cards ?? []).find((card) => card.dataset.taskId === firstTaskId);
    if (!first) return;
    first.focus({ preventScroll: true });
    first.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [collapsed, firstTaskId]);

  const title = (
    <>
      <span className="text-2xl">{column.emoji}</span>
      {column.name}
    </>
  );

  const endZoneId = `end-zone-${column.listId ?? "inbox"}`;
  const { setNodeRef: setEndZoneRef } = useDroppable({
    id: endZoneId,
    data: { type: "end-zone", listId: column.listId ?? null },
  });

  return (
    <div
      data-flip-id={`column-${column.name}`}
      data-flip-column-id={column.name}
      style={{ breakInside: "avoid" }}
    >
      <div
        // Collapsed, the whole card is the list's drop target: a task dragged
        // onto it moves to the end of that list, as the end zone below does.
        ref={collapsed ? setEndZoneRef : undefined}
        className={`bg-warm-bg-card border border-warm-border rounded-card p-5 mb-4 transition-all ${isList ? "shadow-soft-md hover:shadow-soft-lg" : ""} ${column.isShared ? "bg-warm-blue/5" : ""}`}
      >
        <div
          // The arrow keys measure the title by this row, as wide as the cards.
          data-list-header
          className={`flex items-center justify-between group ${collapsed ? "" : "mb-4 pb-3 border-b border-warm-border-subtle"}`}
        >
          <h2 className="font-heading text-lg text-warm-text-primary flex items-center gap-2.5 min-w-0">
            {onToggleCollapse ? (
              <button
                type="button"
                onClick={handleToggleClick}
                aria-expanded={!collapsed}
                aria-controls={collapsed ? undefined : taskListId}
                // A stop for the board's arrow keys, open or collapsed; Enter
                // or Space on it opens or closes the list, as on any button.
                data-list-toggle={column.listId}
                className="flex items-center gap-2.5 min-w-0 text-left rounded-soft hover:text-warm-blue transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-blue focus-visible:ring-offset-2 focus-visible:ring-offset-warm-bg-card"
              >
                <ChevronDown
                  size={16}
                  aria-hidden="true"
                  className={`flex-shrink-0 text-warm-text-tertiary transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`}
                />
                {title}
              </button>
            ) : (
              title
            )}
            {column.isShared && <SharedListMark size={14} />}
          </h2>
          <div className="flex items-center gap-2">
            {onDeleteList && viewMode === "list" && (
              <Tooltip content="Delete list">
                <button
                  onClick={() => onDeleteList(column.name)}
                  aria-label="Delete list"
                  className="text-warm-text-tertiary hover:text-red-500 dark:hover:text-red-400 p-1 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 size={14} />
                </button>
              </Tooltip>
            )}
            <span className="bg-warm-blue/10 text-warm-blue px-2.5 py-0.5 rounded-full text-xs font-bold tabular-nums">
              {visibleCount}
            </span>
            <button
              onClick={() => onAddToColumn(column.name)}
              aria-label={`Add a task to ${column.name}`}
              className="text-warm-text-secondary hover:text-warm-blue dark:hover:text-warm-blue-light hover:bg-warm-bg-elevated p-1.5 rounded-soft transition-all active:scale-95"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {collapsed ? (
          isOverEndZone && <BoardDropIndicator />
        ) : (
          <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
            <div ref={taskListRef} id={taskListId} className="space-y-2.5">
              {column.tasks.map((task, index) => {
                const visible = matchingTaskIds ? matchingTaskIds.has(task.id) : true;
                const isOver = overTaskId === task.id && activeDragTask?.id !== task.id;
                const draggedIndex = activeDragTask ? column.tasks.findIndex((t) => t.id === activeDragTask.id) : -1;
                const showAbove = isOver && (draggedIndex === -1 || draggedIndex > index);
                const showBelow = isOver && draggedIndex !== -1 && draggedIndex < index;

                return (
                  <div
                    key={task.id}
                    className="grid grid-cols-[minmax(0,1fr)] transition-[grid-template-rows,opacity,margin] duration-300 ease-out"
                    style={{
                      gridTemplateRows: visible ? "1fr" : "0fr",
                      opacity: visible ? 1 : 0,
                      pointerEvents: visible ? "auto" : "none",
                      marginTop: visible ? undefined : 0,
                    }}
                  >
                    {/* Only clip vertically while a filtered-out task is collapsing —
                        clipping unconditionally cuts off the card's drop shadow at
                        the card's own edge, which is invisible when another card
                        sits right below but reads as a missing bottom border on
                        the last card in a list (no card below to hide the cut). */}
                    <div className={`overflow-x-clip ${visible ? "" : "overflow-y-hidden"}`}>
                      <div data-flip-id={task.id}>
                        {showAbove && <BoardDropIndicator />}
                        <TaskCard
                          task={task}
                          viewMode={viewMode}
                          suppressTransform={!!activeDragTask}
                          onToggle={onToggleTask}
                          onEdit={onEditTask}
                          onConvertToSubtask={onConvertToSubtask}
                          onBeforeExit={syncFlipPositions}
                          requestExit={exitAnimatingTaskId === task.id}
                          isFocused={focusedTaskId === task.id}
                        />
                        {showBelow && <BoardDropIndicator />}
                      </div>
                    </div>
                  </div>
                );
              })}
              {isOverEndZone && <BoardDropIndicator />}
              {/* Invisible drop zone to capture drops at the end of the list */}
              <div ref={setEndZoneRef} className="h-8" />
            </div>
          </SortableContext>
        )}
      </div>
    </div>
  );
};

export default BoardColumnComponent;
